// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GaugeChartActions.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import { Message as MESSAGE } from 'logic/data/Izenda.ReportPartGaugeConstants';
import React from 'react';
import { size, uniqueId, isNil, concat, nth, head, set, get, getOr, isNumber, last,  } from 'lodash/fp';
const X_AXIS_CONTAINER_NAME = 'labels';
const Y_AXIS_CONTAINER_NAME = 'values';
const SEPARATOR_CONTAINER_NAME = 'separators';
const MAX_DEFAULT_VALUE = 100;
const MIN_DEFAULT_VALUE = 0;
import HighchartUtils from 'share/components/charting/highcharts/Izenda.HighchartUtils';

import ProcessDataUtils from 'share/utils/Izenda.ProcessDataUtils';
import ChartDataParser from 'share/utils/Izenda.ChartDataParser';
import constants from 'logic/data/Izenda.ReportPartChartConstants';
const getTextColor = ChartDataParser.getTextColor;
import reportPartElementUtils from 'share/utils/Izenda.ReportPartElementUtils';

/**
 * create data for gauge
 * @param  {Object} props [description]
 * @return {Object}       [description]
 */
const getChartSeries = (props: any): any => {
  const dataParser = props.chartData;
  const seriesOptions = props.seriesOptions;

  const serieInfo = {
    groups: {},
    charts: []
  };

  const groupCharts = {};
  const dataStructure = dataParser.dataStructure;

  const addChart = (charts: any, groupInfo: any): any => {
    const chartInfos = [];
    charts.forEach(chart => {
      chart.id = uniqueId();
      chart.linkTo = groupInfo;
      serieInfo.charts.push(chart);
      chartInfos.push({
        id: chart.id
      });
    });
    const groupName = get('linkTo.groupName', groupInfo);
    if (groupName !== undefined) {
      isNil(groupCharts[groupName]) && (groupCharts[groupName] = []);
      groupCharts[groupName] = concat(chartInfos, groupCharts[groupName]);
    }
  };

  /**
   * process data by group
   * @param  {object}  groupedData
   * @param  {object}  dataParser
   * @return {object}
   */
  const processDataByGroup = (groupedData: any, dataParser: any): any => {
    const dataStructure = dataParser.dataStructure;

    if (!groupedData || !groupedData.length) {
      return {};
    }

    // find min, max & total for each value (y-axis field)
    const groupInfo = {};

    // init data
    dataStructure[Y_AXIS_CONTAINER_NAME].map(field => {
      groupInfo[field.fieldNameAlias] = {
        columnName: field.columnName,
        fieldName: field.fieldNameAlias,
        min: null,
        max: null,
        sum: 0
      };
    });
    const getDataByRow = (row: any): void => {
      let val, valueInfo;

      dataStructure[Y_AXIS_CONTAINER_NAME].map(field => {
        const isUsingCustomFormat = reportPartElementUtils.checkUsingCustomFormat(field);
        valueInfo = groupInfo[field.fieldNameAlias];
        val = isUsingCustomFormat ? row[constants.CUSTOM_FORMAT_PREFIX + field.columnName] : row[field.columnName];

        valueInfo.sum += val;
        valueInfo.min = isNil(valueInfo.min) ? val : Math.min(valueInfo.min, val);
        valueInfo.max = isNil(valueInfo.max) ? val : Math.max(valueInfo.max, val);
      });
    };
    groupedData.forEach(row => {
      getDataByRow(row);
    });

    return groupInfo;
  };

  /**
   * Create data for each gauge
   * @param  {object}  record
   * @param  {object}  valueMappingField
   * @param  {object}  xAxisField
   * @param  {object}  groupData
   * @param  {object}  otherGroupData
   * @return {object}
   */
  const getDataForChart = (record: Array<any>, valueMappingField: any, xAxisField: any, groupData: any): any => {
    const chartName = valueMappingField.fieldNameAlias;
    const metricInfo = getOr({}, 'properties.metric', valueMappingField.reportPartElm || {});
    const isUsingCustomFormat = reportPartElementUtils.checkUsingCustomFormat(valueMappingField);
    const dataValue = isUsingCustomFormat
      ? record[constants.CUSTOM_FORMAT_PREFIX + valueMappingField.columnName]
      : record[valueMappingField.columnName];
    const textColor = getTextColor(dataValue, get(chartName, props.seriesOptions), {
      element: valueMappingField,
      record
    });

    if (isNil(dataValue)) {
      throw new Error('No data to show');
    }

    const getGroupInfo = (groupData: any, chartName: string): any => {
      groupData = groupData || {};
      let group;

      if (groupData.linkTo) {
        group = groupData.linkTo;
      } else if (groupData.allData) {
        group = groupData.allData;
      } else {
        group = groupData;
      }

      return getOr({}, chartName, group);
    };

    const groupInfo = getGroupInfo(groupData, chartName);

    const getValueFromField = (field: any, dataParser: any, record: Array<any>): any => {
      if (!get('isVisible', field)) {
        return undefined;
      }

      return ProcessDataUtils.getValueOfField(field, dataParser.fieldsMapping, record);
    };

    const getDefinedThresholdValue = (thresholds: any): any => {
      const thresholdsLevels = get('levels', thresholds);

      if (!thresholds || !thresholdsLevels || !thresholdsLevels.length) {
        return [];
      }

      const re = [];
      let isNumberFrom, isNumberTo, from, to;

      thresholdsLevels.forEach(thresold => {
        from = thresold.value || getValueFromField(thresold.element, dataParser, record);
        to = thresold.valueUpper || getValueFromField(thresold.elementUpper, dataParser, record);

        isNumberFrom = isNumber(from);
        isNumberTo = isNumber(to);

        if ((!isNil(from) && !isNumberFrom) || (!isNil(to) && !isNumberTo)) {
          throw new Error(MESSAGE.M02);
        }

        if (isNumberFrom && isNumberTo && from >= to) {
          throw new Error(MESSAGE.M01);
        }

        (isNumberFrom || isNumberTo) &&
          re.push({
            from,
            to,
            color: thresold.color
          });
      });

      return re;
    };
    const definedThreshold = getDefinedThresholdValue(getOr({}, 'thresholds', metricInfo));
    const scale = getScaleInfo(dataValue, metricInfo, groupInfo, definedThreshold);
    /**
     * Check valid threshold
     */
    const isValidThreshold = (thresholds, scale) => {
      if (!thresholds || !thresholds.length) {
        return true;
      }

      let re = true;
      const first = head(thresholds);
      const lastItem =   last(thresholds);
      const thresholdRangeFrom = isNil(first.from) ? first.to : first.from;
      const thresholdRangeTo = isNil(lastItem.to) ? lastItem.from : lastItem.to;

      // threshold is out of scale-range
      if (scale.min > thresholdRangeFrom || scale.max < thresholdRangeTo) {
        return false;
      }

      // threshold should be in order
      let i = 1;
      const thresholdLength = thresholds.length;
      for (i; i < thresholdLength && re; i++) {
        re &= thresholds[i].from >= thresholds[i - 1].to;
      }

      return re;
    };

    if (!isValidThreshold(definedThreshold, scale)) {
      throw new Error(MESSAGE.M04);
    }

    const updateThreshold = (thresholds: Array<any>, scale: any): void => {
      const first = head(thresholds);
      const lastItem =   last(thresholds);

      if (isNil(first.from)) {
        first.from = scale.min;
      }

      if (isNil(lastItem.to)) {
        lastItem.to = scale.max;
      }
    };

    size(definedThreshold) && updateThreshold(definedThreshold, scale);

    /**
     * Get supplementary KPI (simple gauge)
     * @param  {object} supplementaryKPI
     * @param  {object} dataParser
     * @param  {object} record
     * @return {object}
     */
    const getSupplementaryKPI = (supplementaryKPI, dataParser, record) => {
      const field = supplementaryKPI.element || {};
      const fieldName = field.name || '';
      const value = supplementaryKPI.value || getValueFromField(field, dataParser, record);

      if (isNil(value)) {
        return null;
      }
      const jsFormatString = get('properties.dataFormattings.format.jsFormatString', field);
      return {
        name: fieldName,
        value,
        unit: supplementaryKPI.unitLabel,
        dataFormatter(value) {
          if (jsFormatString) {
            const dataType = get('properties.dataFormattings.functionInfo.formatDataType', field);
            const jsFormatId = get('properties.dataFormattings.format.formatId', field);
            return HighchartUtils.formatDataByStringFormat(jsFormatId, value, dataType);
          }

          return getOr(
            value,
            'fomattedValue',
            ChartDataParser.getValueStyle(value, seriesOptions[fieldName], {
              element: field,
              record
            })
          );
        }
      };
    };

    return {
      commonOptions: {
        izendaOptions: {
          showLabels: get('view.showLabels', props.chartOptions),
          autoResized: true,
          scaleFrom: scale.min,
          scaleTo: scale.max,
          serieName: chartName,
          dataUnit: getOr('', 'unitLabel', metricInfo),
          threshold: definedThreshold,
          izendaHiddenAllAxis: true
        },
        series: [
          {
            data: [
              {
                y: dataValue,
                name: xAxisField && record[xAxisField.columnName],
                textColor,
                supplementaryKPI: getSupplementaryKPI(getOr('', 'supplementaryKPI', metricInfo), dataParser, record),
                record
              }
            ],

            xAxisField,
            yAxisField: valueMappingField,
            separatorField: get('linkTo.groupField', groupData),
            separatorValue: get('linkTo.groupName', groupData)
          }
        ]
      }
    };
  };

  /**
   * Create gauge charts
   * @param  {array}  record
   * @param  {object} xAxisField
   * @param  {object}  groupData
   * @param  {object}  otherGroupData
   * @return {array}
   */
  const createGaugeChart = (record: Array<any>, xAxisField: any, groupData: any): Array<any> => {
    const series = dataStructure[Y_AXIS_CONTAINER_NAME].map((valueMappingField: any, index: number) => () =>
      getDataForChart(record, valueMappingField, xAxisField, groupData)
    );

    return series;
  };
  /**
   * Group data by label (in case [function] of field is null)
   * @param  {array} records
   * @param  {object} groupData
   * @param  {object} otherGroupData
   * @return {object}
   */
  const groupDataByLabel = (records: Array<any>, parent: any): void => {
    const groupingField = nth(dataParser.expandedLevel, dataStructure[X_AXIS_CONTAINER_NAME]);
    let groupNames = [];
    let groupedData = dataParser.groupData(groupingField, groupNames, records);
    let groupInfo, groupRecords, charts;

    if (!groupingField) {
      groupNames = ['ALL'];
      groupedData = {
        ALL: groupedData
      };
    }

    groupNames.forEach((groupName: string, groupIndex: number): void => {
      groupRecords = groupedData[groupName];

      groupInfo = $izenda.extend(false, processDataByGroup(groupRecords, dataParser), {
        groupField: groupingField,
        groupName: groupingField ? groupName : '',
        groupIndex,
        isShowPanel: false,
        records: groupRecords
      });

      // link to parent-group
      if (!groupInfo) {
        groupInfo = parent;
      } else {
        parent && (groupInfo = set(parent.groupField ? 'linkTo' : 'allData', parent, groupInfo));
      }

      // create gauge chart
      groupedData[groupName].forEach(record => {
        charts = createGaugeChart(record, groupingField, groupInfo);
        addChart(charts, groupInfo);
      });
    });
  };

  /**
   * Group data by separator field
   * @param  {array} sourceData
   * @return {object}
   */
  const groupDataBySeparator = (): void => {
    const groupingField = head(dataStructure[SEPARATOR_CONTAINER_NAME]);
    let groupNames = [];
    let groupedData = dataParser.groupData(groupingField, groupNames);
    let groupInfo, groupRecords;

    if (!groupingField) {
      groupNames = ['ALL'];
      groupedData = {
        ALL: groupedData
      };
    }

    groupNames.forEach((groupName, groupIndex) => {
      groupRecords = groupedData[groupName];
      groupInfo = $izenda.extend(false, processDataByGroup(groupRecords, dataParser), {
        groupField: groupingField,
        groupName,
        groupIndex,
        isShowPanel: true,
        records: groupRecords
      });

      groupDataByLabel(groupedData[groupName], groupInfo);
    });
  };

  /**
   *
   * @param {number} dataValue
   * @param {object} metricInfo
   * @param {object} groupInfo
   * @param {object} definedThreshold
   */
  function getScaleInfo(dataValue: number, metricInfo: any, groupInfo: any, definedThreshold: any): any {
    dataValue = dataValue || 0;
    groupInfo = groupInfo || {};

    // user defined
    let min = get('scale.from', metricInfo);
    let max = get('scale.to', metricInfo);
    const hasScaleFrom = isNumber(min);
    const hasScaleTo = isNumber(max);
    const hasThreshold = !!size(definedThreshold);
    let firstThreshold, lastThreshold, tempMin, tempMax;

    // scale from/to is undefined >> get min/max from thresholds
    if (isNil(min)) {
      if (hasThreshold) {
        firstThreshold = head(definedThreshold);
        tempMin = firstThreshold.from || firstThreshold.to;
        min = Math.min(groupInfo.min, tempMin);
      } else {
        min = groupInfo.min;
      }
    }

    if (isNil(max)) {
      if (hasThreshold) {
        lastThreshold =   last(definedThreshold);
        tempMax = lastThreshold.from || lastThreshold.to;
        max = Math.max(groupInfo.max, tempMax);
      } else {
        max = groupInfo.max;
      }
    }

    // incase min = max, auto-caculate min/max
    if (isNil(min)) {
      min = dataValue * 0.5;
    }

    if (isNil(max)) {
      max = dataValue * 1.5;
    }

    if (!isNil(min) && min === max) {
      if (min === 0) {
        min = MIN_DEFAULT_VALUE;
        max = MAX_DEFAULT_VALUE;
      } else {
        const bias = Math.ceil(Math.abs(dataValue / 2) || Math.abs(min / 2));
        min = min - bias;
        max = max + bias;
      }
    }

    if (min >= max) {
      if (hasScaleFrom && !hasScaleTo) {
        throw new Error(MESSAGE.M07);
      } else if (!hasScaleFrom && hasScaleTo) {
        throw new Error(MESSAGE.M08);
      }
      throw new Error(MESSAGE.M06);
    }

    return {
      min,
      max
    };
  }

  groupDataBySeparator();
  return {
    charts: serieInfo.charts,
    numberMetrics: size(dataStructure[Y_AXIS_CONTAINER_NAME]),
    groupCharts
  };
};

export default props => ({
  getChartSeries() {
    return getChartSeries(props);
  }
});



// WEBPACK FOOTER //
// ./ui/share/components/charting/Izenda.GaugeChartActions.js